import { InventoryContentOption } from '@openx/types/targeting/inventoryAndContent';
import { ComparisonType } from '@openx/types/targeting/targetingValuesTypes';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useComparisonLabels } from '../../constants';
import { useAppInventoryAllowed } from './featureFlags';

interface UseComparisonLabelForAppInventoryOptions {
  /**
   * The inventory content option type.
   * When APP_GROUP or APP_BUNDLE_ID and App Inventory is allowed,
   * uses "Allowed" / "Blocked" labels instead of legacy "EQUALS" / "DOES NOT EQUAL".
   */
  option?: InventoryContentOption;
}

/**
 * Hook to get comparison label with App Inventory-aware labeling.
 *
 * When App Inventory feature is allowed and the option is APP_GROUP or APP_BUNDLE_ID,
 * returns "Allowed" / "Blocked" instead of legacy "EQUALS" / "DOES NOT EQUAL".
 *
 * @example
 * ```typescript
 * const { getComparisonLabel } = useComparisonLabelForAppInventory({
 *   option: InventoryContentOption.APP_GROUP
 * });
 *
 * // Returns "Allowed" when isAppInventoryAllowed is true
 * const label = getComparisonLabel(ComparisonType.EQUALS);
 * ```
 */
export const useComparisonLabelForAppInventory = ({ option }: UseComparisonLabelForAppInventoryOptions = {}) => {
  const { t } = useTranslation();
  const comparisonLabels = useComparisonLabels();
  const isAppInventoryAllowed = useAppInventoryAllowed();

  const shouldUseAllowBlockLabels =
    isAppInventoryAllowed &&
    (option === InventoryContentOption.APP_GROUP || option === InventoryContentOption.APP_BUNDLE_ID);

  const getComparisonLabel = useCallback(
    (comparisonType: string): string => {
      if (shouldUseAllowBlockLabels) {
        return comparisonType === ComparisonType.EQUALS ? t('Allowed') : t('Blocked');
      }
      return comparisonLabels[comparisonType];
    },
    [shouldUseAllowBlockLabels, comparisonLabels, t]
  );

  return { getComparisonLabel };
};
